/**
 * @file InstallmentSelection.jsx
 * @description Component to manage installments with duplicate handling.
 */
import { MenuItem, Skeleton, TextField, Box, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";

// Helper to create an empty installment
const createEmptyInstallment = (installmentNumber, selectedCourse, selectedCountry) => ({
  id: "",
  countryid: selectedCountry,
  courseid: selectedCourse,
  installmentnumber: installmentNumber,
  installmentamount: "",
  lifecycleid: ""
});

export const InstallmentSelection = ({
  isLoading,
  installments,
  setInstallments,
  lifecycleEvents,
  selectedCourse,
  selectedCountry,
}) => {
  const [installmentData, setInstallmentData] = useState([]);

  // Remove duplicates by keeping the latest occurrence of each installmentnumber
  const removeDuplicates = (data) => {
    const uniqueInstallments = new Map();
    data.forEach((item) => {
      uniqueInstallments.set(item.installmentnumber, item);
    });
    return Array.from(uniqueInstallments.values());
  };

  // Ensure installments 1 to 4 are present, filling missing ones
  const ensureFourInstallments = (existingInstallments) => {
    const uniqueInstallments = removeDuplicates(existingInstallments);
    const installmentMap = new Map(
      uniqueInstallments.map((item) => [item.installmentnumber, item])
    );

    const filledInstallments = [];
    for (let i = 1; i <= 4; i++) {
      if (installmentMap.has(i)) {
        filledInstallments.push(installmentMap.get(i));
      } else {
        filledInstallments.push(createEmptyInstallment(i, selectedCourse, selectedCountry));
      }
    }
    return filledInstallments;
  };

  // Initialize state with clean installment data
  useEffect(() => {
    const initialData = ensureFourInstallments(installments);
    setInstallmentData(initialData);
    setInstallments(initialData); // Sync with parent state
  }, [installments, setInstallments, selectedCourse, selectedCountry]);

  // Handle amount input change
  const handleAmountChange = (index, value) => {
    const sanitizedValue = value.replace(/\D/g, ''); // Remove non-numeric characters

  const updatedInstallments = [...installmentData];
  updatedInstallments[index].installmentamount = sanitizedValue;
  
  setInstallmentData(updatedInstallments);
  setInstallments(updatedInstallments);
  };

  // Handle lifecycle event selection change
  const handleLifecycleChange = (index, value) => {
    const updatedInstallments = [...installmentData];
    updatedInstallments[index].lifecycleid = value;
    setInstallmentData(updatedInstallments);
    setInstallments(updatedInstallments);
  };

  const getOrdinalLabel = (number) => {
    switch (number) {
      case 1: return 'First';
      case 2: return 'Second';
      case 3: return 'Third';
      case 4: return 'Fourth';
      default: return `${number}th`; // Fallback for other numbers
    }
  };

  return (
    <>
      {selectedCourse.length === 0 ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography>Please choose a course to add installments</Typography>
        </Box>
      ) : (
        <Box px={2} overflow="scroll">
          <div className="inst-scroll-wrapper">
            {installmentData.map((installment, index) => (
              <div key={`${installment.installmentnumber}-${index}`}>
                <span className="installment-head">
                  {getOrdinalLabel(installment.installmentnumber)} Installment
                </span>
                <TextField
                  id={`amount-${index}`}
                  placeholder="Enter amount"
                  fullWidth
                  value={installment.installmentamount}
                  onChange={(e) => handleAmountChange(index, e.target.value)}
                  className="inputMdRounded"
                  sx={{ bgcolor: "transparent", width: "45%" }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
                <span style={{ marginLeft: "12px" }}></span>
                <TextField
                  sx={{
                    "& .MuiInputBase-input": {
                      m: 0,
                      p: "4px 8px",
                      width: "100%"                   },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      height: 44,
                    },
                    my: 1,
                    width: "45%",
                  }}
                  value={installment.lifecycleid || ""}
                  select
                  variant="outlined"
                  onChange={(e) => handleLifecycleChange(index, e.target.value)}
                  placeholder="Choose event"
                >
                  {lifecycleEvents.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.eventname}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            ))}
          </div>
        </Box>
      )}
    </>
  );
};
